// ---------------------------------------------------------------------------
// TextTransformService — unified interface for page translation + simplification
// ---------------------------------------------------------------------------
// Uses Google Translate free tier for translation.
// Custom DOM-walker: ONLY sets textNode.data + setAttribute.
// NEVER modifies DOM structure (no wrappers, no node replacements).
// React/Framer-safe.
// ---------------------------------------------------------------------------

export interface TextTransformService {
  translate(targetLang: string): Promise<void>;
  simplify(level: 'einfache' | 'leichte', lang: string): Promise<void>;
  restore(): void;
  isTranslated(): boolean;
  currentLang(): string | null;
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

interface SavedTextNode {
  node: Text;
  original: string;
}

interface SavedAttr {
  el: HTMLElement;
  attr: string;
  original: string;
}

let savedTextNodes: SavedTextNode[] = [];
let savedAttrs: SavedAttr[] = [];
let currentTranslateLang: string | null = null;
let translating = false;
let observer: MutationObserver | null = null;
let observerPaused = false;
let navigationHandler: (() => void) | null = null;
let lastUrl: string = '';

// Translatable HTML attributes (accessibility-relevant)
const TRANSLATABLE_ATTRS = [
  'aria-label', 'aria-description', 'placeholder', 'title', 'alt',
  'aria-placeholder', 'aria-roledescription', 'aria-valuetext',
];

// ---------------------------------------------------------------------------
// Google Translate (free tier, no API key)
// ---------------------------------------------------------------------------

const TRANSLATE_URL = 'https://translate.googleapis.com/translate_a/single';

async function translateSingleText(
  text: string,
  sourceLang: string,
  targetLang: string,
): Promise<string> {
  const params = new URLSearchParams({
    client: 'gtx',
    sl: sourceLang,
    tl: targetLang,
    dt: 't',
    q: text,
  });

  try {
    const res = await fetch(`${TRANSLATE_URL}?${params.toString()}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    if (Array.isArray(data[0])) {
      const parts: string[] = [];
      for (const segment of data[0]) {
        if (Array.isArray(segment) && typeof segment[0] === 'string') {
          parts.push(segment[0]);
        }
      }
      return parts.join('');
    }
    return text;
  } catch {
    return text;
  }
}

/**
 * Batch-translate texts using newline separator.
 * Sending all texts in fewer requests gives Google Translate more context,
 * which produces much better translations for short phrases/headings.
 */
async function translateTexts(
  texts: string[],
  sourceLang: string,
  targetLang: string,
): Promise<string[]> {
  // Use a unique separator that Google Translate won't mangle
  const SEP = '\n\n';
  const BATCH_CHAR_LIMIT = 4000; // Google free tier ~5000 char limit per request
  const results: string[] = new Array(texts.length).fill('');

  let batchStart = 0;
  while (batchStart < texts.length) {
    // Build a batch that fits within char limit
    const batchTexts: string[] = [];
    let charCount = 0;
    let i = batchStart;
    while (i < texts.length) {
      const nextLen = texts[i].length + SEP.length;
      if (charCount + nextLen > BATCH_CHAR_LIMIT && batchTexts.length > 0) break;
      batchTexts.push(texts[i]);
      charCount += nextLen;
      i++;
    }

    // Join with separator, translate as one request
    const joined = batchTexts.join(SEP);
    const translated = await translateSingleText(joined, sourceLang, targetLang);

    // Split result back — Google Translate preserves double-newlines
    const lines = translated.split(/\n\s*\n/);

    for (let j = 0; j < batchTexts.length; j++) {
      results[batchStart + j] = (lines[j] || '').trim() || batchTexts[j];
    }

    batchStart = i;
  }

  return results;
}

// ---------------------------------------------------------------------------
// DOM text extraction (TreeWalker — only reads, never modifies structure)
// ---------------------------------------------------------------------------

function getTranslatableTextNodes(root: Node = document.body): Text[] {
  const walker = document.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode(node: Text) {
        if (!node.textContent || !node.textContent.trim()) return NodeFilter.FILTER_REJECT;
        const parent = node.parentElement;
        if (!parent) return NodeFilter.FILTER_REJECT;
        const tag = parent.tagName;
        if (['SCRIPT', 'STYLE', 'NOSCRIPT', 'CODE', 'PRE', 'TEXTAREA', 'INPUT'].includes(tag)) {
          return NodeFilter.FILTER_REJECT;
        }
        if (parent.closest('accessify-widget') || parent.closest('#accessify-root')) {
          return NodeFilter.FILTER_REJECT;
        }
        if (parent.classList?.contains('notranslate')) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    },
  );

  const nodes: Text[] = [];
  let current: Text | null;
  while ((current = walker.nextNode() as Text | null)) {
    nodes.push(current);
  }
  return nodes;
}

function getTranslatableAttributes(root: Element = document.body): SavedAttr[] {
  const attrs: SavedAttr[] = [];
  const all = root.querySelectorAll<HTMLElement>('*');
  for (const el of all) {
    if (el.closest('accessify-widget') || el.closest('#accessify-root')) continue;
    for (const attr of TRANSLATABLE_ATTRS) {
      const val = el.getAttribute(attr);
      if (val?.trim()) {
        attrs.push({ el, attr, original: val });
      }
    }
  }
  return attrs;
}

// ---------------------------------------------------------------------------
// Apply translations — ONLY textNode.data and setAttribute
// ---------------------------------------------------------------------------

/**
 * Group text nodes by their closest block-level ancestor (h1-h6, p, div, li, etc.)
 * so short fragments inside headings get translated as a whole phrase.
 */
const BLOCK_TAGS = new Set(['H1','H2','H3','H4','H5','H6','P','LI','TD','TH','CAPTION','FIGCAPTION','BLOCKQUOTE','DT','DD']);

interface NodeGroup {
  nodes: Text[];
  fullText: string;            // text to translate (from block element's textContent)
  singleNode: boolean;         // true if group has only one text node (no joining needed)
  charPerNode: boolean;        // true if most nodes are single characters (letter-per-span pattern)
}

function groupTextNodes(textNodes: Text[]): NodeGroup[] {
  const groups: NodeGroup[] = [];
  const assigned = new Set<Text>();

  // First pass: group nodes that share a heading/block parent
  for (const node of textNodes) {
    if (assigned.has(node)) continue;
    const block = findBlockAncestor(node);
    if (block && BLOCK_TAGS.has(block.tagName)) {
      // Collect all text nodes in this group that belong to the same block
      const siblings = textNodes.filter(n => !assigned.has(n) && findBlockAncestor(n) === block);
      if (siblings.length > 1) {
        for (const s of siblings) assigned.add(s);
        // Use block element's textContent for proper concatenation
        // This handles single-char-per-span patterns (e.g. "J E T Z T" → "JETZT")
        const fullText = (block.textContent?.trim() || '').replace(/\s+/g, ' ');
        // Detect letter-per-span pattern: most nodes have 1-2 chars
        const shortNodes = siblings.filter(n => n.data.trim().length <= 2).length;
        const charPerNode = shortNodes > siblings.length * 0.5;
        groups.push({ nodes: siblings, fullText, singleNode: false, charPerNode });
        continue;
      }
    }
    // Single node — no grouping needed
    assigned.add(node);
    groups.push({ nodes: [node], fullText: node.data.trim(), singleNode: true, charPerNode: false });
  }

  return groups;
}

/**
 * Distribute translated text character-by-character back to original text nodes.
 * Preserves the per-character layout (each span keeps getting one character).
 * If the translation is shorter, remaining nodes get empty string.
 * If longer, the last non-empty node gets the overflow.
 */
function distributeCharsToNodes(nodes: Text[], translated: string) {
  // Build a map of which chars in the original go to which node
  // Each node's trimmed text originally held N characters; we replace them 1:1
  const origLengths = nodes.map(n => n.data.trim().length);
  const origWhitespace = nodes.map(n => ({
    leading: n.data.match(/^\s*/)?.[0] || '',
    trailing: n.data.match(/\s*$/)?.[0] || '',
  }));

  let charIdx = 0;
  for (let i = 0; i < nodes.length; i++) {
    const origLen = origLengths[i];
    if (origLen === 0) {
      // Whitespace-only node — preserve as-is
      continue;
    }
    const chunk = translated.slice(charIdx, charIdx + origLen);
    charIdx += origLen;
    nodes[i].data = origWhitespace[i].leading + chunk + origWhitespace[i].trailing;
  }

  // If translation is longer, append overflow to the last node that had content
  if (charIdx < translated.length) {
    const overflow = translated.slice(charIdx);
    for (let i = nodes.length - 1; i >= 0; i--) {
      if (origLengths[i] > 0) {
        nodes[i].data = nodes[i].data.trimEnd() + overflow + origWhitespace[i].trailing;
        break;
      }
    }
  }

  // If translation is shorter, clear nodes that got no characters
  for (let i = 0; i < nodes.length; i++) {
    if (origLengths[i] > 0) {
      const assigned = translated.slice(
        origLengths.slice(0, i).reduce((a, b) => a + b, 0),
        origLengths.slice(0, i + 1).reduce((a, b) => a + b, 0),
      );
      if (!assigned) {
        nodes[i].data = origWhitespace[i].leading + origWhitespace[i].trailing;
      }
    }
  }
}

function findBlockAncestor(node: Text): HTMLElement | null {
  let el = node.parentElement;
  while (el && el !== document.body) {
    if (BLOCK_TAGS.has(el.tagName)) return el;
    el = el.parentElement;
  }
  return null;
}

async function translatePage(targetLang: string): Promise<void> {
  const pageLang = document.documentElement.lang?.split('-')[0] || 'auto';

  // 1. Collect text nodes
  const textNodes = getTranslatableTextNodes();
  const attrEntries = getTranslatableAttributes();

  // Save originals (only nodes we haven't saved yet)
  const existingSaved = new Set(savedTextNodes.map(s => s.node));
  for (const node of textNodes) {
    if (!existingSaved.has(node)) {
      savedTextNodes.push({ node, original: node.data });
    }
  }

  const existingAttrs = new Set(savedAttrs.map(s => `${getElId(s.el)}:${s.attr}`));
  for (const entry of attrEntries) {
    const key = `${getElId(entry.el)}:${entry.attr}`;
    if (!existingAttrs.has(key)) {
      savedAttrs.push(entry);
    }
  }

  // 2. Group text nodes by block ancestor (headings get translated as full phrase)
  const nodeGroups = groupTextNodes(textNodes);

  // 3. Collect all unique texts (grouped + single + attributes)
  const allTexts = [
    ...nodeGroups.map(g => g.fullText),
    ...attrEntries.map(a => a.original.trim()),
  ].filter(Boolean);
  const uniqueTexts = [...new Set(allTexts)];

  if (uniqueTexts.length === 0) return;

  // 4. Translate
  const translated = await translateTexts(uniqueTexts, pageLang, targetLang);

  // 5. Build lookup
  const lookup = new Map<string, string>();
  uniqueTexts.forEach((text, i) => {
    if (translated[i] && translated[i] !== text) {
      lookup.set(text, translated[i]);
    }
  });

  // 6. Apply — pause MutationObserver to prevent feedback loops
  observerPaused = true;

  for (const group of nodeGroups) {
    const replacement = lookup.get(group.fullText);
    if (!replacement) continue;

    if (group.singleNode) {
      // Single node: direct replacement
      const node = group.nodes[0];
      const leading = node.data.match(/^\s*/)?.[0] || '';
      const trailing = node.data.match(/\s*$/)?.[0] || '';
      node.data = leading + replacement + trailing;
    } else if (group.charPerNode) {
      // Letter-per-span pattern: distribute translated characters back across nodes
      // This preserves the per-character styling/animation of the original layout
      distributeCharsToNodes(group.nodes, replacement);
    } else {
      // Multi-node group with word-level nodes: put full translation in first, clear rest
      const firstNode = group.nodes[0];
      const leading = firstNode.data.match(/^\s*/)?.[0] || '';
      firstNode.data = leading + replacement;
      for (let i = 1; i < group.nodes.length; i++) {
        group.nodes[i].data = '';
      }
    }
  }

  for (const entry of attrEntries) {
    const replacement = lookup.get(entry.original.trim());
    if (replacement) {
      entry.el.setAttribute(entry.attr, replacement);
    }
  }

  observerPaused = false;
}

// ---------------------------------------------------------------------------
// MutationObserver — catch framework re-renders + dynamic content
// ---------------------------------------------------------------------------

function setupObserver(targetLang: string) {
  if (observer) observer.disconnect();
  lastUrl = location.href;

  const debouncedTranslate = debounce(() => {
    if (!currentTranslateLang || observerPaused) return;
    translatePage(currentTranslateLang);
  }, 300);

  // Full re-translate on SPA navigation (new page content)
  const onNavigation = () => {
    if (!currentTranslateLang || observerPaused) return;
    const newUrl = location.href;
    if (newUrl !== lastUrl) {
      lastUrl = newUrl;
      // SPA navigation: clear saved nodes (old page), re-translate after content settles
      savedTextNodes = [];
      savedAttrs = [];
      setTimeout(() => {
        if (currentTranslateLang) translatePage(currentTranslateLang);
      }, 500);
    }
  };

  observer = new MutationObserver(mutations => {
    if (observerPaused || !currentTranslateLang) return;
    let hasRelevantChange = false;
    for (const m of mutations) {
      if (m.addedNodes.length > 0) { hasRelevantChange = true; break; }
      if (m.type === 'characterData') { hasRelevantChange = true; break; }
    }
    if (hasRelevantChange) {
      // Check if URL changed (SPA navigation)
      onNavigation();
      debouncedTranslate();
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true,
  });

  // Also listen to popstate (browser back/forward) and pushState
  navigationHandler = onNavigation;
  window.addEventListener('popstate', navigationHandler);
  // Monkey-patch pushState/replaceState to detect programmatic navigation
  const origPush = history.pushState;
  const origReplace = history.replaceState;
  history.pushState = function (...args) {
    origPush.apply(this, args);
    onNavigation();
  };
  history.replaceState = function (...args) {
    origReplace.apply(this, args);
    onNavigation();
  };
}

function teardownObserver() {
  observer?.disconnect();
  observer = null;
  if (navigationHandler) {
    window.removeEventListener('popstate', navigationHandler);
    navigationHandler = null;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getElId(el: HTMLElement): string {
  return el.id || el.getAttribute('data-a11y-id') ||
    (el.setAttribute('data-a11y-id', Math.random().toString(36).substr(2, 9)),
     el.getAttribute('data-a11y-id')!);
}

function debounce(fn: () => void, ms: number): () => void {
  let timer: ReturnType<typeof setTimeout>;
  return () => { clearTimeout(timer); timer = setTimeout(fn, ms); };
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createTextTransformService(): TextTransformService {
  return {
    async translate(targetLang: string): Promise<void> {
      if (translating) return;
      translating = true;

      try {
        // If already translated, restore first
        if (savedTextNodes.length > 0) {
          this.restore();
        }

        await translatePage(targetLang);
        currentTranslateLang = targetLang;

        // Start MutationObserver for dynamic content
        setupObserver(targetLang);
      } finally {
        translating = false;
      }
    },

    async simplify(_level: 'einfache' | 'leichte', _lang: string): Promise<void> {
      // Simplification is handled by the existing AI service per-element
    },

    restore(): void {
      teardownObserver();
      observerPaused = true;

      // Restore text nodes
      for (const saved of savedTextNodes) {
        if (saved.node.parentNode) {
          saved.node.data = saved.original; // NUR .data setzen!
        }
      }
      savedTextNodes = [];

      // Restore attributes
      for (const saved of savedAttrs) {
        if (saved.el.isConnected) {
          saved.el.setAttribute(saved.attr, saved.original);
        }
      }
      savedAttrs = [];

      // Clean up data-a11y-id attributes
      document.querySelectorAll('[data-a11y-id]').forEach(el => {
        el.removeAttribute('data-a11y-id');
      });

      currentTranslateLang = null;
      observerPaused = false;
    },

    isTranslated(): boolean {
      return currentTranslateLang !== null;
    },

    currentLang(): string | null {
      return currentTranslateLang;
    },
  };
}
